/**
 * Client-side CloudFront URL utilities
 *
 * Converts S3 keys to public CloudFront URLs without requiring API calls.
 * Uses NEXT_PUBLIC_CLOUDFRONT_DIST_DOMAIN environment variable.
 */

import { env } from "@repo/env";

// Cache for CloudFront URLs to avoid recalculating
const urlCache = new Map<string, string>();

/**
 * Get the CloudFront distribution domain from environment
 */
function getCloudFrontDomain(): string {
  // Environment-specific domains with fallback
  const domain =
    env.NEXT_PUBLIC_CLOUDFRONT_DIST_DOMAIN_STAGING ||
    env.NEXT_PUBLIC_CLOUDFRONT_DIST_DOMAIN_DEV ||
    env.NEXT_PUBLIC_CLOUDFRONT_DIST_DOMAIN;

  if (!domain) {
    console.warn(
      "[CloudFront] NEXT_PUBLIC_CLOUDFRONT_DIST_DOMAIN is not set. Image URLs may not work.",
    );
    return "";
  }

  return domain;
}

/**
 * Convert an S3 key to a public CloudFront URL
 *
 * @param s3Key - The S3 object key (e.g., "org-logos/abc123.png")
 * @returns The full CloudFront URL, or null if no key provided
 *
 * @example
 * ```ts
 * const url = getCloudFrontUrl("org-logos/my-logo.png");
 * // => "https://d1234.cloudfront.net/org-logos/my-logo.png"
 * ```
 */
export function getCloudFrontUrl(
  s3Key: string | null | undefined,
): string | null {
  if (!s3Key) {
    return null;
  }

  // If it's already a full URL, return as-is (e.g., Brandfetch logos)
  if (s3Key.startsWith("http://") || s3Key.startsWith("https://")) {
    return s3Key;
  }

  // Check cache first
  const cached = urlCache.get(s3Key);
  if (cached) {
    return cached;
  }

  const domain = getCloudFrontDomain();
  if (!domain) {
    return null;
  }

  // Clean the key and construct URL
  const cleanKey = s3Key.replace(/^\/+/, "");
  const url = `https://${domain}/${cleanKey}`;

  // Cache the result
  urlCache.set(s3Key, url);

  return url;
}
